import { useCallback, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  ToastContextProvider,
  type ToastApi,
  type ToastItem,
  type ToastKind,
} from "./useToast";

const AUTO_DISMISS_MS: Record<ToastKind, number> = {
  success: 4000,
  error: 6000,
  info: 4000,
  loading: 0,
};

function toastId(): string {
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const upsert = useCallback((kind: ToastKind, message: string, id?: string) => {
    const nextId = id ?? toastId();
    setItems((prev) => {
      const existing = prev.find((t) => t.id === nextId);
      if (existing) {
        return prev.map((t) => (t.id === nextId ? { ...t, kind, message } : t));
      }
      return [...prev, { id: nextId, kind, message }];
    });
    if (kind !== "loading" && AUTO_DISMISS_MS[kind] > 0) {
      window.setTimeout(() => {
        setItems((prev) => prev.filter((t) => t.id !== nextId));
      }, AUTO_DISMISS_MS[kind]);
    }
    return nextId;
  }, []);

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      success: (message, id) => upsert("success", message, id),
      error: (message, id) => upsert("error", message, id),
      info: (message, id) => upsert("info", message, id),
      loading: (message, id) => upsert("loading", message, id),
      dismiss,
    }),
    [upsert, dismiss],
  );

  return (
    <ToastContextProvider value={api}>
      {children}
      {createPortal(
        <div className="surf-toast-region" aria-live="polite" aria-relevant="additions">
          {items.map((item) => (
            <div
              key={item.id}
              className={`surf-toast surf-toast--${item.kind}`}
              role="status"
              aria-live={item.kind === "error" ? "assertive" : "polite"}
            >
              {item.kind === "loading" && (
                <span className="surf-toast-spinner" aria-hidden>
                  ◌
                </span>
              )}
              <span>{item.message}</span>
              <button
                type="button"
                className="surf-toast-dismiss"
                aria-label="Chiudi notifica"
                onClick={() => dismiss(item.id)}
              >
                ×
              </button>
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContextProvider>
  );
}
